import { DataGrid, useFormatting } from "@msys/ui";
import { Typography } from "@mui/material";
import type { GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { FetchMoreListener } from "../../commons/FetchMoreListener.js";
import type { ChangeLogActionGroup } from "./changeLogGrouping.js";
import { useChangeLogLabels } from "./changeLogLabels.js";
import type { ChangeLogEntityStrategy } from "./changeLogTypes.js";

interface Props {
  groups: ChangeLogActionGroup[];
  hasNextPage: boolean;
  loading: boolean;
  onFetchMore: () => Promise<void> | void;
  onRowClick: (group: ChangeLogActionGroup) => void;
  entityStrategy: ChangeLogEntityStrategy;
}

/**
 * The change log's action list as a `DataGridPremium` (design D2) — one row per
 * `correlationId` group, newest-first. Client column-sort is disabled: order is
 * the server's newest-first, and the rows are the accumulated cache-merged edges.
 * The list is a SAP-CDHDR-style header (design D6): a derived **Action** (a verb-first,
 * colon-qualified label over the group's primary entities after pricing-cascade
 * suppression, never a stored trigger) and a **Target** (the single surviving entity,
 * with its position and name-as-of-action, or the aggregate root). The former value-free
 * Change column is gone — the per-field tri-state old→new values live only in the detail
 * modal. Infinite scroll uses the shared `FetchMoreListener` intersection sentinel rather
 * than the grid's `onRowsScrollEnd`: the grid is `autoHeight` (no internal scroll
 * viewport), so `onRowsScrollEnd` never fires — the page scroll drives loading.
 */
export function ChangeLogActionsGrid({
  groups,
  hasNextPage,
  loading,
  onFetchMore,
  onRowClick,
  entityStrategy,
}: Props) {
  const { t } = useTranslate(["ChangeLog", "Global"]);
  const { getFormattedDateTime } = useFormatting();
  const labels = useChangeLogLabels();

  const columns = React.useMemo<GridColDef<ChangeLogActionGroup>[]>(
    () => [
      {
        field: "occurredAt",
        headerName: t("Time", { ns: "Global" }),
        sortable: false,
        disableColumnMenu: true,
        width: 170,
        renderCell: ({ row }) => getFormattedDateTime(row.occurredAt),
      },
      {
        field: "actor",
        headerName: t("User", { ns: "Global" }),
        sortable: false,
        disableColumnMenu: true,
        width: 180,
        renderCell: ({ row }) => labels.actorLabel(row.actor),
      },
      {
        field: "action",
        headerName: t("Action", { ns: "Global" }),
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        minWidth: 200,
        // The composed Action label (design D3): a verb-first, colon-qualified string over
        // the group's primary entities (cascade by-products suppressed), never a stored
        // trigger. Truncates with an ellipsis; the full text is in the tooltip.
        renderCell: ({ row }) => {
          const text = labels.actionLabel(row);
          return (
            <Typography
              variant="body2"
              noWrap
              title={text}
              sx={{ width: "100%" }}
            >
              {text}
            </Typography>
          );
        },
      },
      {
        field: "target",
        headerName: t("Target", { ns: "ChangeLog" }),
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        minWidth: 180,
        // The Target entity (design D6): the single surviving primary entity with its
        // position + name-as-of-action, or the aggregate root. Truncates with an ellipsis;
        // the full text is in the tooltip.
        renderCell: ({ row }) => {
          const text = labels.targetDisplay(row, entityStrategy).label;
          return (
            <Typography
              variant="body2"
              noWrap
              title={text}
              sx={{ width: "100%" }}
            >
              {text}
            </Typography>
          );
        },
      },
    ],
    [t, getFormattedDateTime, labels, entityStrategy]
  );

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {t("No changes recorded yet", { ns: "ChangeLog" })}
      </Typography>
    );
  }

  return (
    <>
      <DataGrid<ChangeLogActionGroup>
        columns={columns}
        rows={groups}
        getRowId={row => row.id}
        autoHeight
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        loading={loading}
        onRowClick={({ row }) => onRowClick(row)}
        // Cells take keyboard focus, but the grid synthesizes no row click from Enter or
        // Space — so the detail modal, reachable only through `onRowClick`, would be closed
        // to keyboard-only users. `cellKeyDown` is the grid's own per-cell key event and
        // carries the row, so the focused cell activates the very same handler.
        onCellKeyDown={(
          params: GridCellParams<ChangeLogActionGroup>,
          event: React.KeyboardEvent<HTMLElement>
        ) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          // Space would otherwise scroll the page instead of opening the action.
          event.preventDefault();
          onRowClick(params.row);
        }}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
      />
      <FetchMoreListener enabled={hasNextPage} fetchMore={onFetchMore} />
    </>
  );
}
